import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X, ShoppingCart, Package, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatPrice } from '../utils/formatPrice';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to add to cart
const addToCart = (product) => {
  const cart = JSON.parse(localStorage.getItem('spa_cart') || '[]');
  const existingIndex = cart.findIndex(item => item.slug === product.slug);
  
  if (existingIndex >= 0) {
    cart[existingIndex].quantity = (cart[existingIndex].quantity || 1) + 1;
  } else {
    cart.push({
      slug: product.slug,
      name: product.name,
      price: product.price_ttc,
      image: product.images?.[0] || '',
      quantity: 1,
      category: product.main_category || 'general'
    });
  }
  
  localStorage.setItem('spa_cart', JSON.stringify(cart));
  window.dispatchEvent(new Event('cartUpdated'));
  
};

// Normalize text for search
const normalizeText = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .trim();
};

// Search scoring function
const getSearchScore = (item, searchTerms, isProduct = true) => {
  let score = 0;
  const name = normalizeText(item.name);
  const brand = normalizeText(item.brand);
  const sku = normalizeText(item.sku);
  const model = normalizeText(item.model);
  const subCategory = normalizeText(item.sub_category);
  
  for (const term of searchTerms) {
    if (sku === term || model === term) score += 100;
    else if (sku?.includes(term) || model?.includes(term)) score += 50;
    if (name?.includes(term)) {
      score += 30;
      if (name.startsWith(term) || name.includes(` ${term}`)) score += 20;
    }
    if (brand?.includes(term)) score += 25;
    if (subCategory?.includes(term)) score += 15;
  }
  if (isProduct) score += 10;
  return score;
};

/**
 * Mobile Search Component - Full screen search for mobile
 */
const MobileSearch = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ parts: [], spas: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [allParts, setAllParts] = useState([]);
  const [allSpas, setAllSpas] = useState([]);
  
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partsRes, spasRes] = await Promise.all([
          axios.get(`${API}/parts`),
          axios.get(`${API}/spas`)
        ]);
        setAllParts(partsRes.data || []);
        setAllSpas(spasRes.data || []);
      } catch (error) {
        console.error('Error fetching search data:', error);
      }
    };
    fetchData();
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    // Reset search when closing
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults({ parts: [], spas: [] });
    }
  }, [isOpen]);

  // Perform search
  const performSearch = useCallback((query) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults({ parts: [], spas: [] });
      return;
    }

    setIsSearching(true);
    const searchTerms = normalizeText(query).split(/\s+/).filter(t => t.length >= 2);
    
    const partsResults = allParts
      .map(item => ({ item, score: getSearchScore(item, searchTerms, true) }))
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map(r => r.item);
    
    const spasResults = allSpas
      .map(item => ({ item, score: getSearchScore(item, searchTerms, false) }))
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map(r => r.item);
    
    setSearchResults({ parts: partsResults, spas: spasResults });
    setIsSearching(false);
  }, [allParts, allSpas]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => performSearch(searchQuery), 200);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  const closeAndNavigate = (path) => {
    onClose();
    navigate(path);
  };

  const totalResults = searchResults.parts.length + searchResults.spas.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-white" data-testid="mobile-search">
      {/* Search Header */}
      <div className="sticky top-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center gap-3 p-4">
          <div className="flex-1 flex items-center gap-3 bg-slate-100 rounded-xl px-4 py-3">
            <Search className={`w-5 h-5 flex-shrink-0 ${isSearching ? 'text-[#F97316] animate-pulse' : 'text-slate-400'}`} />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher une pièce, un spa..."
              className="flex-1 text-base text-slate-900 placeholder-slate-400 bg-transparent focus:outline-none"
              autoComplete="off"
              data-testid="mobile-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="p-1 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-3 py-2 text-[#1E3A5F] font-medium"
            data-testid="mobile-search-close"
          >
            Annuler
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto pb-20">
        {searchQuery.length >= 2 ? (
          totalResults > 0 ? (
            <>
              {/* Parts Results */}
              {searchResults.parts.length > 0 && (
                <div>
                  <div className="px-4 py-3 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider sticky top-0">
                    Pièces & Accessoires ({searchResults.parts.length})
                  </div>
                  {searchResults.parts.map((item) => (
                    <div 
                      key={item.slug}
                      className="flex items-center gap-3 px-4 py-4 border-b border-slate-100 active:bg-slate-50"
                      onClick={() => closeAndNavigate(`/pieces-accessoires/${item.slug}`)}
                    >
                      <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                        {item.images?.[0] ? (
                          <img 
                            src={item.images[0]} 
                            alt={item.name}
                            className="w-full h-full object-contain p-1"
                          />
                        ) : (
                          <Package className="w-6 h-6 text-slate-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500 uppercase">{item.brand}</p>
                        <h4 className="text-sm font-medium text-slate-900 line-clamp-2">{item.name}</h4>
                        {item.sku && <p className="text-xs text-slate-400">Réf: {item.sku}</p>}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        {item.price_ttc ? (
                          <div className="flex flex-col items-end gap-2">
                            <span className="text-sm font-bold text-slate-900">{formatPrice(item.price_ttc)}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                addToCart(item);
                              }}
                              className="p-2 bg-[#1E3A5F] hover:bg-[#152C4A] text-white rounded-lg"
                            >
                              <ShoppingCart className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-500">Sur devis</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Spas Results */}
              {searchResults.spas.length > 0 && (
                <div>
                  <div className="px-4 py-3 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider sticky top-0">
                    Spas ({searchResults.spas.length})
                  </div>
                  {searchResults.spas.map((spa) => (
                    <div 
                      key={spa.slug}
                      className="flex items-center gap-3 px-4 py-4 border-b border-slate-100 active:bg-slate-50"
                      onClick={() => closeAndNavigate(`/spas/${spa.slug}`)}
                    >
                      <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                        {spa.images?.[0] ? (
                          <img 
                            src={spa.images[0]} 
                            alt={spa.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Package className="w-6 h-6 text-slate-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500 uppercase">{spa.brand}</p>
                        <h4 className="text-sm font-medium text-slate-900 line-clamp-2">{spa.name}</h4>
                        <p className="text-xs text-slate-400">{spa.places} places</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              )}

              {/* See all link */}
              <div className="p-4">
                <button
                  onClick={() => closeAndNavigate(`/pieces-detachees?q=${encodeURIComponent(searchQuery)}`)}
                  className="w-full py-3 bg-slate-100 rounded-xl text-sm text-[#1E3A5F] font-medium flex items-center justify-center gap-2"
                >
                  Voir tous les résultats
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 font-medium mb-1">Aucun résultat</p>
              <p className="text-sm text-slate-500">
                Essayez avec une autre référence ou mot-clé
              </p>
            </div>
          )
        ) : (
          <div className="p-8 text-center">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 font-medium mb-2">Recherche rapide</p>
            <p className="text-sm text-slate-500 mb-6">
              Tapez au moins 2 caractères pour rechercher une pièce, un accessoire ou un spa
            </p>
            
            {/* Quick links */}
            <div className="space-y-2 max-w-xs mx-auto">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-3">Suggestions</p>
              <Link 
                to="/pieces-detachees" 
                onClick={onClose}
                className="block px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 hover:bg-slate-100"
              >
                Pièces détachées
              </Link>
              <Link 
                to="/accessoires-spa" 
                onClick={onClose}
                className="block px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 hover:bg-slate-100"
              >
                Accessoires spa
              </Link>
              <Link 
                to="/filtres-spa" 
                onClick={onClose}
                className="block px-4 py-3 bg-slate-50 rounded-xl text-sm text-slate-700 hover:bg-slate-100"
              >
                Filtres spa
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileSearch;
